import { useState, useEffect } from 'react';
import { createReferral, getMyReferrals, sendReferralInvite } from '../services/referralService';
import { getSettings } from '../utils/storage';

export default function Referrals({ onBack, user, onToast }) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);

  const settings = getSettings();
  const referrerName = settings.profile?.name || 'A friend';

  useEffect(() => {
    loadReferrals();
  }, []);

  async function loadReferrals() {
    setLoading(true);
    const { referrals: data } = await getMyReferrals();
    setReferrals(data);
    setLoading(false);
  }

  async function handleSend() {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      onToast('Please enter a valid email address');
      return;
    }

    setSending(true);
    const { code, error } = await createReferral(trimmed);
    if (error) {
      onToast(error);
      setSending(false);
      return;
    }

    // Send the invite email
    await sendReferralInvite(trimmed, code, referrerName);

    setEmail('');
    onToast('Invite sent!');
    setSending(false);
    loadReferrals();
  }

  const acceptedCount = referrals.filter(r => r.status === 'accepted').length;
  const pendingCount = referrals.filter(r => r.status === 'pending').length;

  return (
    <div className="screen" style={{ paddingBottom: 100 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          onClick={onBack}
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: 'var(--bg-elevated)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div style={{ flex: 1 }}>
          <h1 className="screen-title" style={{ fontSize: '1.25rem' }}>Referrals</h1>
          {acceptedCount > 0 && (
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              {acceptedCount} accepted &middot; {acceptedCount} bonus vehicle{acceptedCount !== 1 ? 's' : ''} unlocked
            </p>
          )}
        </div>
      </div>

      {/* How it works */}
      <div style={{
        background: 'var(--bg-elevated)',
        borderRadius: 'var(--radius-sm, 8px)',
        padding: '16px',
        marginBottom: 20,
      }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 8px' }}>
          How it works
        </h3>
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
          <p style={{ margin: '0 0 6px' }}>1. Enter your friend's email below</p>
          <p style={{ margin: '0 0 6px' }}>2. They'll receive an invite to Proper Miles</p>
          <p style={{ margin: 0 }}>3. Once they sign up, you unlock an extra vehicle slot</p>
        </div>
      </div>

      {/* Email input */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>
          Friend's email
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="friend@example.com"
            onKeyDown={(e) => e.key === 'Enter' && !sending && handleSend()}
            style={{
              flex: 1,
              padding: '12px 14px',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm, 8px)',
              color: 'var(--text-primary)',
              fontSize: '0.9375rem',
              outline: 'none',
            }}
          />
          <button
            onClick={handleSend}
            disabled={sending || !email.trim()}
            style={{
              padding: '12px 20px',
              background: sending || !email.trim() ? 'var(--bg-elevated)' : 'var(--accent)',
              color: sending || !email.trim() ? 'var(--text-muted)' : '#fff',
              border: 'none',
              borderRadius: 'var(--radius-sm, 8px)',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: sending || !email.trim() ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
              opacity: sending ? 0.7 : 1,
            }}
          >
            {sending ? 'Sending...' : 'Send Invite'}
          </button>
        </div>
        {pendingCount >= 5 && (
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>
            You've reached the maximum of 5 pending referrals. Wait for some to be accepted.
          </p>
        )}
      </div>

      {/* Referral list */}
      <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>
        Your referrals
      </h3>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
          Loading...
        </div>
      ) : referrals.length === 0 ? (
        <div style={{
          background: 'var(--bg-elevated)',
          borderRadius: 'var(--radius-sm, 8px)',
          padding: '32px 20px',
          textAlign: 'center',
        }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 0 }}>
            No referrals yet. Invite a friend to get started!
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {referrals.map((ref) => (
            <div
              key={ref.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 16px',
                background: 'var(--bg-elevated)',
                borderRadius: 'var(--radius-sm, 8px)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '0.875rem',
                  color: 'var(--text-primary)',
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {ref.referee_email}
                </div>
                <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  Sent {new Date(ref.created_at).toLocaleDateString()}
                  {ref.accepted_at && ` \u00B7 Accepted ${new Date(ref.accepted_at).toLocaleDateString()}`}
                </div>
              </div>
              <span style={{
                fontSize: '0.6875rem',
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: 12,
                background: ref.status === 'accepted'
                  ? 'rgba(34, 197, 94, 0.15)'
                  : 'rgba(234, 179, 8, 0.15)',
                color: ref.status === 'accepted' ? '#22c55e' : '#eab308',
                textTransform: 'capitalize',
                flexShrink: 0,
                marginLeft: 12,
              }}>
                {ref.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
